/**
 * NCAA Tournament Brackets - Modal System
 * Reusable modal component for confirmations and alerts
 * Uses aria-hidden approach consistent with auth modals
 */

(function() {
    'use strict';

    // ========================================
    // Modal System
    // ========================================

    /**
     * Create and show a confirmation modal
     * @param {Object} options - Modal options
     * @param {string} options.title - Modal title
     * @param {string} options.message - Modal message/body content
     * @param {string} options.confirmText - Confirm button text (default: 'Confirm')
     * @param {string} options.cancelText - Cancel button text (default: 'Cancel')
     * @param {string} options.confirmClass - Additional class for confirm button (e.g., 'btn-danger')
     * @param {Function} options.onConfirm - Callback when confirmed
     * @param {Function} options.onCancel - Callback when cancelled (optional)
     * @returns {Object} Modal controller with close method
     */
    function showModal(options) {
        const defaults = {
            title: 'Confirm',
            message: 'Are you sure?',
            confirmText: 'Confirm',
            cancelText: 'Cancel',
            confirmClass: '',
            onConfirm: function() {},
            onCancel: function() {}
        };

        const settings = { ...defaults, ...options };

        // Remove any existing modal
        const existingModal = document.querySelector('.ncaa-modal.ncaa-confirm-modal');
        if (existingModal) {
            existingModal.remove();
        }

        // Get plugin URL for assets
        const pluginUrl = (typeof ncaaBrackets !== 'undefined' && ncaaBrackets.pluginUrl) ? ncaaBrackets.pluginUrl : '';
        const closeIconUrl = pluginUrl ? `${pluginUrl}assets/img/x.svg` : '';
        const closeIconHtml = closeIconUrl ? `<img src="${closeIconUrl}" alt="Close" />` : '&times;';

        // Create modal HTML (using aria-hidden approach like auth modals)
        const modalHTML = `
            <div class="ncaa-modal ncaa-confirm-modal" aria-hidden="true">
                <div class="ncaa-modal-overlay"></div>
                <div class="ncaa-modal-container">
                    <div class="ncaa-modal-header">
                        <h3 class="ncaa-modal-title">${settings.title}</h3>
                        <button type="button" class="ncaa-modal-close" aria-label="Close">${closeIconHtml}</button>
                    </div>
                    <div class="ncaa-modal-content">
                        <p>${settings.message}</p>
                    </div>
                    <div class="ncaa-modal-footer">
                        <button type="button" class="btn ncaa-modal-btn-cancel">${settings.cancelText}</button>
                        <button type="button" class="btn btn-secondary ncaa-modal-btn-confirm ${settings.confirmClass}">${settings.confirmText}</button>
                    </div>
                </div>
            </div>
        `;

        // Insert modal into DOM
        document.body.insertAdjacentHTML('beforeend', modalHTML);

        const modal = document.querySelector('.ncaa-modal.ncaa-confirm-modal');
        const overlay = modal.querySelector('.ncaa-modal-overlay');
        const closeBtn = modal.querySelector('.ncaa-modal-close');
        const cancelBtn = modal.querySelector('.ncaa-modal-btn-cancel');
        const confirmBtn = modal.querySelector('.ncaa-modal-btn-confirm');

        // Prevent body scroll when modal is open
        document.body.classList.add('modal-open');

        // Trigger reflow to enable transition
        modal.offsetHeight;

        // Show modal with animation
        requestAnimationFrame(() => {
            modal.setAttribute('aria-hidden', 'false');
        });

        // Close modal function
        function closeModal(confirmed) {
            modal.setAttribute('aria-hidden', 'true');

            // Wait for animation to complete before removing
            setTimeout(() => {
                modal.remove();
                document.body.classList.remove('modal-open');

                if (confirmed) {
                    settings.onConfirm();
                } else {
                    settings.onCancel();
                }
            }, 200);
        }

        // Event listeners
        closeBtn.addEventListener('click', () => closeModal(false));
        cancelBtn.addEventListener('click', () => closeModal(false));
        confirmBtn.addEventListener('click', () => closeModal(true));

        // Close on overlay click
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) {
                closeModal(false);
            }
        });

        // Close on Escape key
        function handleEscape(e) {
            if (e.key === 'Escape') {
                closeModal(false);
                document.removeEventListener('keydown', handleEscape);
            }
        }
        document.addEventListener('keydown', handleEscape);

        // Focus confirm button
        confirmBtn.focus();

        // Return controller
        return {
            close: () => closeModal(false)
        };
    }

    /**
     * Show a simple alert modal (no cancel button)
     * @param {Object} options - Modal options
     * @param {string} options.title - Modal title
     * @param {string} options.message - Modal message
     * @param {string} options.buttonText - Button text (default: 'OK')
     * @param {Function} options.onClose - Callback when closed
     */
    function showAlert(options) {
        const defaults = {
            title: 'Alert',
            message: '',
            buttonText: 'OK',
            onClose: function() {}
        };

        const settings = { ...defaults, ...options };

        // Remove any existing modal
        const existingModal = document.querySelector('.ncaa-modal.ncaa-alert-modal');
        if (existingModal) {
            existingModal.remove();
        }

        // Get plugin URL for assets
        const pluginUrl = (typeof ncaaBrackets !== 'undefined' && ncaaBrackets.pluginUrl) ? ncaaBrackets.pluginUrl : '';
        const closeIconUrl = pluginUrl ? `${pluginUrl}assets/img/x.svg` : '';
        const closeIconHtml = closeIconUrl ? `<img src="${closeIconUrl}" alt="Close" />` : '&times;';

        // Create modal HTML (using aria-hidden approach like auth modals)
        const modalHTML = `
            <div class="ncaa-modal ncaa-alert-modal" aria-hidden="true">
                <div class="ncaa-modal-overlay"></div>
                <div class="ncaa-modal-container">
                    <div class="ncaa-modal-header">
                        <h3 class="ncaa-modal-title">${settings.title}</h3>
                        <button type="button" class="ncaa-modal-close" aria-label="Close">${closeIconHtml}</button>
                    </div>
                    <div class="ncaa-modal-content">
                        <p>${settings.message}</p>
                    </div>
                    <div class="ncaa-modal-footer">
                        <button type="button" class="btn btn-secondary ncaa-modal-btn-confirm">${settings.buttonText}</button>
                    </div>
                </div>
            </div>
        `;

        // Insert modal into DOM
        document.body.insertAdjacentHTML('beforeend', modalHTML);

        const modal = document.querySelector('.ncaa-modal.ncaa-alert-modal');
        const overlay = modal.querySelector('.ncaa-modal-overlay');
        const closeBtn = modal.querySelector('.ncaa-modal-close');
        const confirmBtn = modal.querySelector('.ncaa-modal-btn-confirm');

        // Prevent body scroll when modal is open
        document.body.classList.add('modal-open');

        // Trigger reflow to enable transition
        modal.offsetHeight;

        // Show modal with animation
        requestAnimationFrame(() => {
            modal.setAttribute('aria-hidden', 'false');
        });

        // Close modal function
        function closeModal() {
            modal.setAttribute('aria-hidden', 'true');

            setTimeout(() => {
                modal.remove();
                document.body.classList.remove('modal-open');
                settings.onClose();
            }, 200);
        }

        // Event listeners
        closeBtn.addEventListener('click', closeModal);
        confirmBtn.addEventListener('click', closeModal);

        // Close on overlay click
        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) {
                closeModal();
            }
        });

        // Close on Escape key
        function handleEscape(e) {
            if (e.key === 'Escape') {
                closeModal();
                document.removeEventListener('keydown', handleEscape);
            }
        }
        document.addEventListener('keydown', handleEscape);

        // Focus button
        confirmBtn.focus();
    }

    // Expose to global scope
    window.ncaaModals = {
        show: showModal,
        alert: showAlert
    };

})();
